import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Typography } from 'antd';
import { isAxiosError } from 'axios';
import type {
  CreateWebhookIntegrationLimitExceededErrorDto,
  SendTestWebhookEventErrorDto
} from '@api/Api';
import { apiClient } from '@api/client';
import CopyableInput from '@components/CopyableInput/CopyableInput';
import type { IntegrationInstallInstructionsProps } from '@pages/AdminSettings/components/IntegrationTab/constants';
import {
  ALL_EVENT_VALUES,
  EventTypesInput,
  HeadersInput,
  SignatureVerificationGuide,
  WebhookHeader,
  WebhookEventType,
  normalizeWebhookUrl,
  validateWebhookHeaders,
  webhookHeadersToPayload,
  validateWebhookUrl
} from '@pages/AdminSettings/components/IntegrationTab/webhook-shared';
import {
  SetupLayout,
  SetupSection
} from '@pages/AdminSettings/components/IntegrationTab/setup-layout';
import { IntegrationsWebhookIcon } from '@icons';

const { Text } = Typography;

interface CreateWebhookForm {
  url: string;
  eventTypes: WebhookEventType[];
  headers: WebhookHeader[];
}

interface CreatedWebhookState {
  integrationId: string;
  secret: string;
  url: string;
  headers: WebhookHeader[];
}

export default function WebhookInstallInstructions({
  organizationId,
  onClose
}: IntegrationInstallInstructionsProps) {
  const [form] = Form.useForm<CreateWebhookForm>();
  const [createdWebhook, setCreatedWebhook] =
    useState<CreatedWebhookState | null>(null);
  const [createWebhookErrorDescription, setCreateWebhookErrorDescription] =
    useState<string>('');

  const handleUrlPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text');
    const normalized = normalizeWebhookUrl(pastedText);

    if (normalized !== pastedText) {
      event.preventDefault();
      form.setFieldValue('url', normalized);
    }
  };

  const normalizeUrlInForm = () => {
    const currentValue = form.getFieldValue('url') as string | undefined;
    if (!currentValue) {
      return;
    }

    form.setFieldValue('url', normalizeWebhookUrl(currentValue));
  };

  const createWebhook = useMutation({
    mutationFn: async (values: CreateWebhookForm) => {
      const { data } =
        await apiClient.webhook.webhookControllerCreateIntegration({
          organizationId,
          url: values.url,
          eventTypes: ALL_EVENT_VALUES.every((eventType) =>
            values.eventTypes.includes(eventType)
          )
            ? null
            : values.eventTypes,
          headers: webhookHeadersToPayload(values.headers)
        });
      return {
        integrationId: data.integrationId,
        secret: data.secret,
        url: values.url,
        eventTypes: values.eventTypes,
        headers: values.headers
      };
    },
    onError: (error) => {
      if (
        isAxiosError(error) &&
        error.response?.data?.errorCode === 'webhook_integration_limit_exceeded'
      ) {
        const payload = error.response
          .data as CreateWebhookIntegrationLimitExceededErrorDto;
        const { limit } = payload;
        setCreateWebhookErrorDescription(
          `You have reached the maximum number of webhook integrations (${limit}).`
        );
        return;
      }
      setCreateWebhookErrorDescription(
        'Oops! Something went wrong while creating the webhooks integration.'
      );
    },
    onSuccess: (data) => {
      setCreateWebhookErrorDescription('');
      setCreatedWebhook(data);
    }
  });

  const sendTestEvent = useMutation({
    mutationFn: async () => {
      if (!createdWebhook) return;
      await apiClient.webhook.webhookControllerSendTestEvent({
        integrationId: createdWebhook.integrationId,
        url: createdWebhook.url,
        headers: webhookHeadersToPayload(createdWebhook.headers)
      });
    }
  });

  const testEventError = useMemo(() => {
    const err = sendTestEvent.error;
    if (!err) return null;
    if (isAxiosError(err)) {
      const payload = err.response?.data;
      if (payload?.errorCode === 'send_test_event_error') {
        const data = payload as SendTestWebhookEventErrorDto;
        return `Webhook endpoint responded with status ${data.statusCode}${
          data.statusText ? `(${data.statusText})` : ''
        }`;
      }
      return `Unknown error occurred`;
    }
  }, [sendTestEvent.error]);

  const progress = {
    current: createdWebhook ? 1 : 0,
    items: ['Endpoint', 'Signing secret']
  };

  if (createdWebhook) {
    return (
      <SetupLayout
        logo={IntegrationsWebhookIcon}
        title="Webhooks are connected"
        subtitle="StreamWork now sends the events you chose to your endpoint."
        progress={progress}
        footer={
          <Button type="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <SetupSection title="Endpoint">
          <div className="integration_setup-card">
            <div className="integration_setup-row">
              <div className="integration_setup-row-text">
                <Text ellipsis={{ tooltip: createdWebhook.url }}>
                  {createdWebhook.url}
                </Text>
                <Text type="secondary">
                  Send a test event to check that your endpoint receives it.
                </Text>
              </div>
              <div className="integration_setup-row-control">
                <Button
                  onClick={() => sendTestEvent.mutate()}
                  loading={sendTestEvent.isPending}
                >
                  Send test event
                </Button>
              </div>
            </div>
          </div>
          {sendTestEvent.isSuccess ? (
            <Alert
              type="success"
              showIcon
              message="Test event sent successfully."
            />
          ) : null}
          {testEventError ? (
            <Alert
              type="error"
              showIcon
              message="Couldn’t send the test event"
              description={testEventError}
            />
          ) : null}
        </SetupSection>

        <SetupSection
          title="Signing secret"
          description="Strongly recommended: validate signatures, so your endpoint knows each request comes from StreamWork and not from someone else."
        >
          <CopyableInput readOnly value={createdWebhook.secret} />
          <Alert
            type="warning"
            showIcon
            message="Save this secret now"
            description="It isn’t shown again. Keep it in your password manager or secrets vault."
          />
        </SetupSection>

        <SetupSection title="Validating signatures">
          <div>
            <SignatureVerificationGuide />
          </div>
        </SetupSection>
      </SetupLayout>
    );
  }

  return (
    <SetupLayout
      logo={IntegrationsWebhookIcon}
      title="Set up webhooks"
      subtitle="StreamWork calls your endpoint when the events you choose happen."
      progress={progress}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={createWebhook.isLoading}
            onClick={() => form.submit()}
          >
            Create webhooks integration
          </Button>
        </>
      }
    >
      {createWebhook.isError && (
        <Alert
          type="error"
          showIcon
          message="Couldn’t create the webhooks integration"
          description={createWebhookErrorDescription}
        />
      )}

      <Form<CreateWebhookForm>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          url: '',
          eventTypes: ALL_EVENT_VALUES,
          headers: []
        }}
        onFinish={(values) => createWebhook.mutate(values)}
      >
        <Form.Item
          name="url"
          label="Endpoint URL"
          rules={[
            { required: true, message: 'Endpoint URL is required' },
            {
              validator: (_, value: string | undefined) =>
                validateWebhookUrl(value)
            }
          ]}
          extra="A public HTTPS endpoint: your backend, Zapier, Make.com or n8n."
        >
          <Input
            placeholder="https://example.com/webhooks/streamwork"
            onPaste={handleUrlPaste}
            onBlur={normalizeUrlInForm}
          />
        </Form.Item>

        <Form.Item
          name="headers"
          label="Custom headers"
          validateTrigger={['onBlur', 'onSubmit']}
          rules={[
            {
              validator: (_, value: WebhookHeader[] | undefined) =>
                validateWebhookHeaders(value)
            }
          ]}
          extra="Optional. Added to each webhook request."
        >
          <HeadersInput />
        </Form.Item>

        <Form.Item
          name="eventTypes"
          style={{ marginBottom: 0 }}
          rules={[
            {
              required: true,
              type: 'array',
              min: 1,
              message: 'Select at least one event'
            }
          ]}
        >
          <EventTypesInput />
        </Form.Item>
      </Form>
    </SetupLayout>
  );
}
